import type { Font, FontFamily } from '@/api/types/font-family'
import type { Paragraph } from '@/api/types/stowed-element'
import type { FontContextValue } from '@/providers/font'
import type { GetVariantFn } from '../types/font-lookup'

export function collectAllFontIds(paragraphs: Paragraph[]): number[] {
  const ids: number[] = []

  for (const p of paragraphs) {
    if (!p.textLines) continue
    for (const line of p.textLines) {
      if (!line.textRuns) continue
      for (const run of line.textRuns) {
        if (run.fontId != null) ids.push(run.fontId)
      }
    }
  }

  return [...new Set(ids)]
}

export function isMixedByStyleName(fontIds: number[], getVariant: GetVariantFn): boolean {
  if (fontIds.length <= 1) return false
  const first = getVariant(fontIds[0])?.fontStyleName

  return fontIds.some((id) => getVariant(id)?.fontStyleName !== first)
}

export function getFirstResolvedFontFamilyId(
  paragraphs: Paragraph[] | undefined,
  getVariant: GetVariantFn
): number | undefined {
  if (!paragraphs) return undefined
  for (const p of paragraphs) {
    if (!p.textLines) continue
    for (const line of p.textLines) {
      if (!line.textRuns) continue
      for (const run of line.textRuns) {
        const famId = run.fontFamilyId ?? getVariant(run.fontId)?.fontFamilyId

        if (famId != null) return famId
      }
    }
  }

  return undefined
}

export function isResolvedFontFamilyMixed(
  paragraphs: Paragraph[] | undefined,
  getVariant: GetVariantFn
): boolean {
  if (!paragraphs) return false
  let first: number | undefined = undefined
  let found = false

  for (const p of paragraphs) {
    if (!p.textLines) continue
    for (const line of p.textLines) {
      if (!line.textRuns) continue
      for (const run of line.textRuns) {
        const famId = run.fontFamilyId ?? getVariant(run.fontId)?.fontFamilyId

        if (famId == null) continue
        if (!found) {
          first = famId
          found = true
          continue
        }

        if (famId !== first) return true
      }
    }
  }

  return false
}

/** A family's faces in their catalog style order (Regular, Italic, Bold, …). */
export function sortVariantsByStyleOrder(fonts: Font[]): Font[] {
  return [...fonts].sort(
    (a, b) => (a.fontStyle?.fontStyleOrder ?? 0) - (b.fontStyle?.fontStyleOrder ?? 0)
  )
}

/**
 * The style names every family in a mixed-family selection has in common, in the first family's
 * style order — the Style select's options when the selection spans more than one family.
 */
export function listSharedStyleNames(
  fontIds: number[],
  getVariant: FontContextValue['getVariantByFontId'],
  getFamily: (fontFamilyId: number) => FontFamily | undefined
): Array<{ styleName: string; order: number }> {
  const familyIds = [
    ...new Set(
      fontIds.map((id) => getVariant(id)?.fontFamilyId).filter((id): id is number => id != null)
    ),
  ]
  const loadedFamilies = familyIds
    .map((id) => getFamily(id))
    .filter((f): f is NonNullable<typeof f> => f != null)

  if (loadedFamilies.length === 0) return []

  const firstFonts = loadedFamilies[0]!.fonts ?? []
  const styleNames = firstFonts
    .map((f) => f.fontStyle?.fontStyleName)
    .filter((n): n is string => !!n)
    .filter((name) =>
      loadedFamilies.every((fam) =>
        (fam.fonts ?? []).some((f) => f.fontStyle?.fontStyleName === name)
      )
    )

  return styleNames
    .map((name) => ({
      styleName: name,
      order:
        firstFonts.find((f) => f.fontStyle?.fontStyleName === name)?.fontStyle?.fontStyleOrder ?? 0,
    }))
    .sort((a, b) => a.order - b.order)
}
